import { INBOUND_UNKNOWN_PART_LABEL_SKU } from "./inboundReceiveMpn";

export type OpenSubcontainerLine = {
  source_item_id: string;
  sku: string;
  quantity: number;
  label_description?: string | null;
};

export type ClosedSubcontainerItem = {
  source_item_id: string;
  sku: string;
  quantity: number;
};

/**
 * Quantity already sealed in other containers of this line stays put.
 * replaceOpenQuantity replaces the unsealed amount. Otherwise the entry is added to it.
 * An open amount of zero means every contents container is sealed, so the entry is the new unsealed quantity.
 */
export function receivedQuantityAfterContainerEntry(input: {
  alreadySealedQuantity: number;
  currentReceivedQuantity: number;
  enteredQuantity: number;
  replaceOpenQuantity: boolean;
}): number {
  const sealed = Math.max(0, Math.trunc(input.alreadySealedQuantity) || 0);
  const received = Math.max(0, Math.trunc(input.currentReceivedQuantity) || 0);
  const entered = Math.max(0, Math.trunc(input.enteredQuantity) || 0);
  const open = Math.max(0, received - sealed);
  const nextOpen = input.replaceOpenQuantity ? entered : open + entered;
  return sealed + nextOpen;
}

export function openSubcontainerLines(input: {
  lines: readonly {
    source_item_id: string;
    sku: string;
    received_quantity: number;
    unknown_mpn_quantity?: number;
    scanned_mpn?: string | null;
  }[];
  closedItems: readonly ClosedSubcontainerItem[];
}): OpenSubcontainerLine[] {
  const open: OpenSubcontainerLine[] = [];
  for (const line of input.lines) {
    const lineId = String(line.source_item_id);
    const received = Number(line.received_quantity);
    if (Number.isFinite(received) && received > 0) {
      const closed = closedQuantityForLineSku(lineId, line.sku, input.closedItems);
      const quantity = received - closed;
      if (quantity > 0) {
        open.push({ source_item_id: lineId, sku: line.sku, quantity });
      }
    }
    const unknownQuantity = Number(line.unknown_mpn_quantity ?? 0);
    if (Number.isFinite(unknownQuantity) && unknownQuantity > 0) {
      const closedUnknown = closedQuantityForLineSku(
        lineId,
        INBOUND_UNKNOWN_PART_LABEL_SKU,
        input.closedItems,
      );
      const quantity = unknownQuantity - closedUnknown;
      if (quantity > 0) {
        open.push({
          source_item_id: lineId,
          sku: INBOUND_UNKNOWN_PART_LABEL_SKU,
          quantity,
          label_description: line.scanned_mpn?.trim() || null,
        });
      }
    }
  }
  return open;
}

export function closedQuantityForLine(
  sourceItemId: string,
  closedItems: readonly ClosedSubcontainerItem[],
): number {
  return closedQuantityForLineSku(sourceItemId, "", closedItems, { anySku: true });
}

export function closedQuantityForLineSku(
  sourceItemId: string,
  sku: string,
  closedItems: readonly ClosedSubcontainerItem[],
  options?: { anySku?: boolean },
): number {
  const lineId = String(sourceItemId);
  const normalizedSku = sku.trim().toLowerCase();
  let total = 0;
  for (const item of closedItems) {
    if (String(item.source_item_id) !== lineId) continue;
    if (
      !options?.anySku &&
      normalizedSku !== "" &&
      String(item.sku ?? "").trim().toLowerCase() !== normalizedSku
    ) {
      continue;
    }
    total += Number(item.quantity);
  }
  return total;
}

export const RECEIVE_OPEN_CONTAINER_BLOCK_MESSAGE =
  "Seal the open container before choosing a part.";

/** While a container is open, the part list is locked. Quantity is corrected on that container. */
export function receiveOpenContainerSelection(openSkuCount: number): "start-new" | "blocked" {
  return openSkuCount > 0 ? "blocked" : "start-new";
}

export function receiveArrivalScanShouldOpenOtherOrder(input: {
  currentSourceType: string | null | undefined;
  currentSourceId: string | null | undefined;
  resolved: { source_type: string; source_id: string } | null;
  resolvedSources?: readonly { source_type: string; source_id: string }[];
}): boolean {
  const sources =
    input.resolvedSources != null && input.resolvedSources.length > 0
      ? input.resolvedSources
      : input.resolved == null
        ? []
        : [input.resolved];
  if (sources.length === 0) return false;
  const currentType = input.currentSourceType ?? "";
  const currentId = String(input.currentSourceId ?? "").trim();
  if (currentType === "" || currentId === "") return true;
  return !sources.some(
    (source) => source.source_type === currentType && String(source.source_id) === currentId,
  );
}
